import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';

// Types
import { Subscription } from 'Shared/constants/types';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useSubscriptionTracking } from 'Shared/tracking/use-subscription-tracking';

// Helpers
import { showNotificationError } from 'Shared/helpers';

// Actions
import { cancelSubscription } from 'Shared/actions/Subscriptions.actions';

// Components
import { WpContainer, WpContainerBody, WpContainerHeader, WpContainerFooter } from 'Wpds/components/container';
import { WpButton } from 'Wpds/components/button';
import PlanSubscriptionDetails from 'Shared/components/PlanSubscriptionDetails/PlanSubscriptionDetails';
import WpToastManager from 'Wpds/components/toast/WpToastManager';
import ConfirmModal from 'Shared/components/ConfirmModal/ConfirmModal';

type Props = {
  subscription?: Subscription;
};

const PlanSubscription = ({ subscription }: Props) => {
  const dispatch = useDispatch();
  const { trackClickedCancelSubscription } = useSubscriptionTracking();
  const [isShowDowngradeModal, toggleIsShowDowngradeModal] = useToggle(false);
  const [isDowngradingPlanProcesssing, toggleIsDowngradingPlanProcesssing] = useToggle(false);

  const hasSubscription = Boolean(subscription);
  const isStartup = hasSubscription && subscription.plan_name === 'Startup Plan';
  const isGrowth = hasSubscription && subscription.plan_name === 'Growth Plan';

  const handleCancelSubscription = useCallback(() => {
    toggleIsDowngradingPlanProcesssing();
    dispatch(cancelSubscription())
      .then(() => {
        WpToastManager.success('Plan Cancelled.');
        window.location.reload();
      })
      .catch(e => {
        showNotificationError(e);
      });
  }, [dispatch]);

  const handleCancelSubscriptionClick = () => {
    trackClickedCancelSubscription();
    toggleIsShowDowngradeModal();
  };

  const baseClassName = 'PlanSubscription';

  return (
    <>
      <WpContainer className={baseClassName}>
        <WpContainerHeader>
          <h5>Your Plan</h5>
        </WpContainerHeader>
        <WpContainerBody>
          <PlanSubscriptionDetails subscription={subscription} />
        </WpContainerBody>
        {!isGrowth && (
          <WpContainerFooter>
            {isStartup && (
              <WpButton onClick={handleCancelSubscriptionClick} variant="tertiary">
                Cancel Plan
              </WpButton>
            )}
          </WpContainerFooter>
        )}
      </WpContainer>
      <ConfirmModal
        message="Are you sure you want to cancel your subscription?"
        isOpen={isShowDowngradeModal}
        onToggle={toggleIsShowDowngradeModal}
        onConfirmClick={handleCancelSubscription}
        confirmButtonLabel="Yes, cancel"
        isProcessing={isDowngradingPlanProcesssing}
        cancelButtonLabel="Back"
      />
    </>
  );
};

export default PlanSubscription;
